"use client";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useAuth } from "./AuthContext";
import { checkCredentials } from "@/lib/api/instacart-auth";
import { getLocal, setLocal } from "@/lib/storage";
import { generateFiltersOnLink } from "./utils/generate-filters-on-link";
import { getViews } from "@/lib/api/views";
import { useRouter } from "next/navigation";

interface InstacartAuthContextType {
  isInstacartLinked: boolean;
  lastCredentialsCheck: number | null;
  isLoading: boolean;
  setInstacartLinked: (linked: boolean) => void;
  checkInstacartLink: (forceCheck?: boolean) => Promise<boolean>;
}

const InstacartAuthContext = createContext<
  InstacartAuthContextType | undefined
>(undefined);

const LOCAL_STORAGE_KEY = "instacartAuth";
const CREDENTIALS_CHECK_INTERVAL = 60000;

export function InstacartAuthProvider({ children }: { children: ReactNode }) {
  const { sessionId, logout } = useAuth();
  const router = useRouter();
  const [isInstacartLinked, setIsInstacartLinked] = useState(false);
  const [lastCredentialsCheck, setLastCredentialsCheck] = useState<
    number | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    const localData = getLocal(LOCAL_STORAGE_KEY, {
      isInstacartLinked: false,
      lastCredentialsCheck: null,
    });
    setIsInstacartLinked(localData.isInstacartLinked);
    setLastCredentialsCheck(localData.lastCredentialsCheck);

    if (sessionId) {
      setIsLoading(true);
      checkInstacartLink(true).finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [sessionId]);

  // Save to localStorage when state changes
  useEffect(() => {
    if (!isLoading) {
      setLocal(LOCAL_STORAGE_KEY, { isInstacartLinked, lastCredentialsCheck });
    }
  }, [isInstacartLinked, lastCredentialsCheck, isLoading]);

  const canCheckCredentials = (
    minInterval: number = CREDENTIALS_CHECK_INTERVAL
  ) => {
    if (!lastCredentialsCheck) return true;
    return Date.now() - lastCredentialsCheck >= minInterval;
  };

  const checkInstacartLink = async (
    forceCheck: boolean = false
  ): Promise<boolean> => {
    if (!sessionId) return false;
    if (!forceCheck && !canCheckCredentials()) return isInstacartLinked;

    try {
      const response = await checkCredentials(sessionId);
      const now = Date.now();
      setLastCredentialsCheck(now);

      if (response.statusCode === 400 && response.body === "Invalid session") {
        logout();
        setIsInstacartLinked(false);
        return false;
      }

      const hasValidTokens =
        response.statusCode === 200 && response.body && response.body.tokens;
      setIsInstacartLinked(Boolean(hasValidTokens));
      return Boolean(hasValidTokens);
    } catch (error) {
      setLastCredentialsCheck(Date.now());
      return isInstacartLinked;
    }
  };
  const setInstacartLinked = (linked: boolean) => {
    setIsInstacartLinked(linked);
    if (linked && sessionId) {
      let hasRedirected = false;
      
      const safeRedirect = (path: string) => {
        if (!hasRedirected) {
          hasRedirected = true;
          router.push(path);
        }
      };
      // Generate filters in background
      generateFiltersOnLink(sessionId)
        .then((response) => {
          // Only redirect if filters were generated AND batchDetails is not empty
          if (response.filtersGenerated && response.shouldRedirectToSwipe) {
            safeRedirect("/swipe");
          }
        })
        .catch((error) => {
          console.error("Error generating filters on link:", error);
        });

      // Check views in background and redirect if needed
      getViews(sessionId)
        .then((response) => {
          if (response.views?.has_swiped_filters === false) {
            safeRedirect("/swipe");
          }
        })
        .catch((error) => {
          console.error("Error checking views:", error);
        });
    }
  };

  return (
    <InstacartAuthContext.Provider
      value={{
        isInstacartLinked,
        lastCredentialsCheck,
        isLoading,
        setInstacartLinked,
        checkInstacartLink,
      }}
    >
      {children}
    </InstacartAuthContext.Provider>
  );
}

export function useInstacartAuth() {
  const context = useContext(InstacartAuthContext);
  if (!context)
    throw new Error(
      "useInstacartAuth must be used within an InstacartAuthProvider"
    );
  return context;
}
